import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useEffect,
  useMemo,
  useState,
} from "react";
import * as THREE from "three";
import { useThree, useFrame } from "@react-three/fiber";
import { useGLTF } from "@react-three/drei";
import {
  buildPartComposite,
  canvasToTexture,
  buildLayersSignature,
} from "./pipeline.js";
import { GuidePaint, guidePaintRef } from "./GuidePaint";
import { useSnapshot } from "valtio";
import { onTTQuality } from "./ttQuality.js"; // adjust path
import { KTX2Loader } from "three/examples/jsm/loaders/KTX2Loader.js";

let ktx2Loader = null;

function getKTX2Loader(gl) {
  if (ktx2Loader) return ktx2Loader;

  ktx2Loader = new KTX2Loader()
    .setTranscoderPath(`${import.meta.env.BASE_URL}basis/`)
    .detectSupport(gl);

  return ktx2Loader;
}

/* ---------- tiny helpers ---------- */
function makeWhiteTex() {
  const data = new Uint8Array([255, 255, 255, 255]);
  const tex = new THREE.DataTexture(data, 1, 1);
  tex.needsUpdate = true;
  tex.flipY = false;
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

function disableRaycast(group) {
  if (!group) return;
  group.traverse((n) => {
    if (n?.isMesh) n.raycast = () => null;
  });
}

function computeMinYFromMeshes(group) {
  const box = new THREE.Box3();
  const tmpBox = new THREE.Box3();
  const tmpMat = new THREE.Matrix4();

  let any = false;

  group.traverse((obj) => {
    if (!obj.isMesh) return;

    const name = obj.name || "";

    // Ignore stuff that should NOT define "feet on floor"
    if (/PaintProxy/i.test(name)) return;
    if (name.startsWith("GARMENT_")) return;
    if (name.startsWith("HAIR_")) return;

    // Only use the body/skin meshes to define ground
    if (!(name.startsWith("BODY") || name.startsWith("SKIN"))) return;

    const geom = obj.geometry;
    if (!geom) return;

    if (!geom.boundingBox) geom.computeBoundingBox();
    tmpBox.copy(geom.boundingBox);

    // transform geometry bounds into world space
    tmpMat.copy(obj.matrixWorld);
    tmpBox.applyMatrix4(tmpMat);

    if (!any) {
      box.copy(tmpBox);
      any = true;
    } else {
      box.union(tmpBox);
    }
  });

  return any ? box : null;
}

export function isIPad() {
  if (typeof navigator === "undefined") return false;

  const ua = navigator.userAgent || "";
  const platform = navigator.platform || "";

  const isIpadUA = /iPad/.test(ua);
  const isMacTouch =
    platform === "MacIntel" &&
    typeof navigator.maxTouchPoints === "number" &&
    navigator.maxTouchPoints > 1;

  return isIpadUA || isMacTouch;
}

export function isProbablyMobile() {
  if (isIPad()) return true;
  return /Mobi|Android|iPhone|iPod/i.test(navigator.userAgent || "");
}

function pickCompositeSize({ quality = "high", gl }) {
  const maxTex = gl?.capabilities?.maxTextureSize || 4096;
  const ipad = isIPad();
  const mobile = isProbablyMobile();

  const hi = Math.min(maxTex, 4096);

  if (quality === "drag") {
    if (ipad) return 1024;
    return mobile ? 1024 : 1536;
  }

  // High quality, but iPad gets a cap to avoid crashes
  if (ipad) return Math.min(2048, hi);
  return mobile ? Math.min(2048, hi) : hi;
}

function aoBaseUrl() {
  // Works on dev, prod, and share links (subfolder deploys)
  return `${import.meta.env.BASE_URL}tattoo/ao/`;
}

function garmentAoPathsFor({ sex, appearance }) {
  const base = aoBaseUrl();
  const s = sex === "male" ? "male" : "female";

  const topKey = appearance?.top || "none";
  const bottomKey = appearance?.bottom || "none";

  const top = topKey !== "none" ? `${base}${s}/top__${topKey}_ao.png` : null;

  const bottom =
    bottomKey !== "none" ? `${base}${s}/bottom__${bottomKey}_ao.png` : null;

  return { top, bottom };
}

// FEMALE ONLY: if top+bottom are none, use an underwear AO as the "base"
function baseAoOverrideFor({ sex, appearance }) {
  const s = sex === "male" ? "male" : "female";
  if (s !== "female") return null;

  const topKey = appearance?.top || "none";
  const bottomKey = appearance?.bottom || "none";

  if (topKey === "none" && bottomKey === "none") {
    return `${aoBaseUrl()}female/base__underwear_ao.png`;
  }
  return null;
}

// ✅ ADD THIS NEW FUNCTION RIGHT HERE
function hairAoPathFor({ sex, appearance }) {
  const s = sex === "male" ? "male" : "female";
  const base = `${import.meta.env.BASE_URL}tattoo/ao/${s}/`;

  const hairKey = appearance?.hair;
  if (!hairKey || hairKey === "none") return null;

  return `${base}hair__${hairKey}_ao.jpg`;
}

function isPaintProxy(o) {
  const n = o?.name || "";
  return (
    o?.userData?.ttRole === "paintProxy" ||
    o?.userData?.ttSkipOverlay === true ||
    /PaintProxy/i.test(n)
  );

  function applyClothingVisibility(root, appearance) {
    if (!root) return;

    const top = appearance?.top ?? "none";
    const bottom = appearance?.bottom ?? "none";

    root.traverse((obj) => {
      const n = obj.name || "";

      // ----- TOP -----
      if (n === "UNDERWEAR_TOP") {
        obj.visible = top === "none";
        return;
      }
      if (n.startsWith("GARMENT_TOP__")) {
        obj.visible = top !== "none" && n.endsWith(top);
        return;
      }

      // ----- BOTTOM -----
      if (n === "UNDERWEAR_BOTTOM") {
        obj.visible = bottom === "none";
        return;
      }
      if (n.startsWith("GARMENT_BOTTOM__")) {
        obj.visible = bottom !== "none" && n.endsWith(bottom);
        return;
      }
    });
  }
}

function aoPathForAppearance(appearance, sex) {
  const s = sex === "male" ? "male" : "female";
  const base = `${import.meta.env.BASE_URL}tattoo/ao/${s}/`; // ✅ sex folder

  const top =
    appearance?.top && appearance.top !== "none"
      ? `${base}top__${appearance.top}_ao.png`
      : null;

  const bottom =
    appearance?.bottom && appearance.bottom !== "none"
      ? `${base}bottom__${appearance.bottom}_ao.png`
      : null;

  // ✅ special: female base AO when top === "none"
  const femaleUnderwearBase =
    s === "female" && (!appearance?.top || appearance.top === "none")
      ? `${base}base__underwear_ao.png`
      : null;

  return { top, bottom, femaleUnderwearBase };
}

async function loadImage(url) {
  return await new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = (e) => reject(new Error(`Failed to load image: ${url}`));
    img.src = url;
  });
}

// Multiply (baseOverride OR baseAO) * (topAO?) * (bottomAO?) into a CanvasTexture
async function buildStackedAoTexture({
  baseAoTex,
  baseOverrideUrl,
  topUrl,
  bottomUrl,
}) {
  if (!baseAoTex) return null;

  // ✅ if baseOverrideUrl exists, use it as the starting layer instead of GLB AO
  let baseDrawable = baseAoTex.image;
  if (baseOverrideUrl) {
    const img = await loadImage(baseOverrideUrl);
    baseDrawable = img;
  }
  if (!baseDrawable) return null;

  const w = baseDrawable.width;
  const h = baseDrawable.height;
  if (!w || !h) return null;

  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d", { willReadFrequently: false });

  // start with base
  ctx.globalCompositeOperation = "source-over";
  ctx.globalAlpha = 1;
  ctx.drawImage(baseDrawable, 0, 0, w, h);

  // multiply garments
  ctx.globalCompositeOperation = "multiply";
  if (topUrl) {
    const topImg = await loadImage(topUrl);
    ctx.drawImage(topImg, 0, 0, w, h);
  }
  if (bottomUrl) {
    const botImg = await loadImage(bottomUrl);
    ctx.drawImage(botImg, 0, 0, w, h);
  }

  const tex = new THREE.CanvasTexture(canvas);
  tex.flipY = false;
  tex.colorSpace = THREE.NoColorSpace;
  tex.needsUpdate = true;
  return tex;
}

function forceRestPose(sc) {
  let skinned = null;
  sc.traverse((o) => {
    if (!skinned && o?.isSkinnedMesh && o.skeleton) skinned = o;
  });

  try {
    skinned?.skeleton?.pose?.();
  } catch {}

  try {
    sc.updateMatrixWorld?.(true);
  } catch {}
}

async function loadResizedTexture(
  url,
  maxSize = 1024,
  { forceCanvas = false } = {}
) {
  const res = await fetch(url, { mode: "cors", cache: "force-cache" });
  const blob = await res.blob();

  // helper to apply consistent settings
  const finalize = (tex, { hi = false } = {}) => {
    tex.flipY = false;
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.generateMipmaps = false;
    tex.minFilter = THREE.LinearFilter;
    tex.magFilter = THREE.LinearFilter;
    baseTex.anisotropy = 1;
    tex.needsUpdate = true;
    return tex;
  };

  // --- Try ImageBitmap path first (unless forcing canvas) ---
  if (!forceCanvas) {
    try {
      const bmp = await createImageBitmap(blob);
      const w = bmp?.width || 0;
      const h = bmp?.height || 0;

      if (w && h) {
        const s = Math.min(1, maxSize / Math.max(w, h));
        const rw = Math.max(1, Math.round(w * s));
        const rh = Math.max(1, Math.round(h * s));

        // already small enough
        if (s >= 1) {
          return finalize(new THREE.Texture(bmp), { hi: true });
        }

        // resize (may be ignored on iPad — we’ll detect via size check)
        try {
          const resized = await createImageBitmap(blob, {
            resizeWidth: rw,
            resizeHeight: rh,
            resizeQuality: "low",
          });

          // if resize got ignored, fall back to canvas
          if (
            resized.width === w &&
            resized.height === h &&
            (rw !== w || rh !== h)
          ) {
            throw new Error("resize ignored");
          }

          return finalize(new THREE.Texture(resized), { hi: false });
        } catch {
          // fall through to canvas
        }
      }
    } catch {
      // fall through to canvas
    }
  }

  // --- Canvas fallback (most reliable on iPad) ---
  const img = await new Promise((resolve, reject) => {
    const im = new Image();
    im.crossOrigin = "anonymous";
    im.onload = () => resolve(im);
    im.onerror = reject;
    im.src = URL.createObjectURL(blob);
  });

  const iw = img.naturalWidth || img.width || 1;
  const ih = img.naturalHeight || img.height || 1;
  const s = Math.min(1, maxSize / Math.max(iw, ih));
  const rw = Math.max(1, Math.round(iw * s));
  const rh = Math.max(1, Math.round(ih * s));

  const c = document.createElement("canvas");
  c.width = rw;
  c.height = rh;
  const ctx = c.getContext("2d");
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "low";
  ctx.drawImage(img, 0, 0, rw, rh);

  URL.revokeObjectURL(img.src);

  return finalize(new THREE.CanvasTexture(c), { hi: s >= 1 });
}
async function makeDownrezCanvasTexture(url, maxSize) {
  const img = await new Promise((resolve, reject) => {
    const im = new Image();
    im.crossOrigin = "anonymous";
    im.onload = () => resolve(im);
    im.onerror = reject;
    im.src = url;
  });

  const iw = img.width || 1;
  const ih = img.height || 1;
  const s = Math.min(1, maxSize / Math.max(iw, ih));
  const cw = Math.max(1, Math.round(iw * s));
  const ch = Math.max(1, Math.round(ih * s));

  const c = document.createElement("canvas");
  c.width = cw;
  c.height = ch;
  const ctx = c.getContext("2d");
  ctx.drawImage(img, 0, 0, cw, ch);

  const tex = new THREE.CanvasTexture(c);
  tex.flipY = false;
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.generateMipmaps = false;
  tex.minFilter = THREE.LinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.anisotropy = 1;
  tex.needsUpdate = true;
  return tex;
}

const __ktxSeen = new Set();

const PartModel = forwardRef(function PartModel(
  {
    modelUrl,
    nodeNameRegex = /.*/i,
    skinColor = "#d19771",

    poseWeights = {}, // { "POSE__Neutral": 1, "POSE__Hand on Hip": 0, ... }
    onPoseMorphNames,

    skinRoughness = 0.6,
    skinMetalness = 0.0,
    skinEnvIntensity = 0.35,
    skinNormalScale = 9.55,

    overlayOpacity = 1.0,
    edgeClip = 0.8,
    targetHeight = 1.5,
    yOffset = -0.5,
    uvImage,
    tattooKey,
    partKey = "torso",
    layers = [],
    onReady,
    contextSex,
    contextPart,
  },
  ref
) {
  const rootRef = useRef();
  const { gl, camera } = useThree();

  const [ttQuality, setTtQuality] = useState("high"); // "high" | "low"
  const [qualityTick, setQualityTick] = useState(0); // forces rebuild
  const [matsReadyTick, setMatsReadyTick] = useState(0);

  const { scene } = useGLTF(modelUrl, true, true, (loader) => {
    const ktx2 = getKTX2Loader(gl);
    loader.setKTX2Loader(ktx2);
  });
  console.log(
    "[garments] nodes",
    scene.getObjectByName("GARMENT_BOTTOM__jeans"),
    scene.getObjectByName("GARMENT_TOP__tshirt")
  );

  useEffect(() => {
    console.log("[PartModel] ttQuality =", ttQuality);
  }, [ttQuality]);

  useEffect(() => {
    // default to high
    setTtQuality("high");

    return onTTQuality((level) => {
      setTtQuality(level === "low" ? "low" : "high");
      setQualityTick((n) => n + 1); // ✅ make signature change
    });
  }, []);

  useEffect(() => {
    const fn = () => setQualityTick((x) => x + 1);
    window.addEventListener("tt:quality", fn);
    return () => window.removeEventListener("tt:quality", fn);
  }, []);

  const HAIR_PRESET_MAP = {
    black: "#29241e",
    brown: "#3b3024",
    blonde: "#cfa36a",
    blondeGray: "#9c8d73",
    golden: "#554828",
    platinum: "#a99c84",
    gray: "#c7c7c7",
    red: "#7a3d2a",
  };

  const GARMENT_PRESETS = {
    black: "#111111",
    white: "#f2f2f2",
    charcoal: "#2a2a2a",
    navy: "#1f2a44",
    olive: "#3b4a2a",
    red: "#7a2a2a",
    denim: "#2a3b66",
    tan: "#b08b5a",
  };

  function resolveHairHex(snap) {
    const mode = snap.appearance?.hairColorMode || "preset";
    if (mode === "custom") return snap.appearance?.hairColorCustom || "#3b2a1a";
    if (mode === "skin") return snap.skinColor || "#d19771";
    const preset = snap.appearance?.hairColorPreset || "brown";
    return HAIR_PRESET_MAP[preset] || "#3b2a1a";
  }

  function resolveGarmentHex(mode, preset, custom) {
    if (mode === "custom") return custom || "#111111";
    return GARMENT_PRESETS[preset] || "#111111";
  }

  const snap = useSnapshot(tattoo);
  const quality = snap.renderQuality || "high";
  const rq = snap.renderQuality || "high";
  const rqTick = snap.renderQualityTick || 0;

  const dragLatchRef = useRef(false);
  const dragLastSeenRef = useRef(0);
  const lastHitRef = useRef([0, 0, 0]);

  const lastHitWorldRef = useRef([0, 0, 0]);
  const movingMsRef = useRef(0);
  const weForcedDragRef = useRef(false);

  const lastDragSeenAtRef = useRef(0);
  const dragLowResUntilRef = useRef(0);

  const dragPreviewTexHiRef = useRef(null);
  const dragPreviewTexLoRef = useRef(null);
  const dragPreviewUrlHiRef = useRef(null);
  const dragPreviewUrlLoRef = useRef(null);

  const dragLoTexRef = useRef(null);
  const dragHiTexRef = useRef(null);
  const dragLoUrlRef = useRef(null);
  const dragHiUrlRef = useRef(null);

  // Re-apply garment visibility whenever scene OR appearance changes
  React.useEffect(() => {
    if (!scene) return;

    const a = snap.appearance || {};
    setVisibleByPrefix(scene, "GARMENT_TOP__", a.top || "none");
    setVisibleByPrefix(scene, "GARMENT_BOTTOM__", a.bottom || "none");
    setVisibleByPrefix(scene, "HAIR__", a.hair || "none");

    console.log("[garments] applied", a);
    console.count("[AO] push run");
  }, [
    scene,
    snap.appearance?.top,
    snap.appearance?.bottom,
    snap.appearance?.hair,
  ]);

  const baseScene = useMemo(() => scene.clone(true), [scene]);

  useEffect(() => {
    applyClothingVisibility(baseScene, snap.appearance);
  }, [baseScene, snap.appearance?.top, snap.appearance?.bottom]);

  console.log("[garments] snap.appearance", snap.appearance);
  // --- garment toggles (Step 1) ---
  function setVisibleByPrefix(root, prefix, activeKey) {
    if (!root) return;
    root.traverse((obj) => {
      const name = obj?.name || "";
      if (!name.startsWith(prefix)) return;

      if (!activeKey || activeKey === "none") {
        obj.visible = false;
        return;
      }

      const suffix = name.slice(prefix.length);
      obj.visible = suffix === activeKey;
    });
  }

  useEffect(() => {
    if (!rootRef.current) return;

    // only once per load
    if (rootRef.current.userData.__tt_grounded) return;
    rootRef.current.userData.__tt_grounded = true;

    groundRootToY0(rootRef.current);
  }, [baseScene]); // or whatever load trigger you have

  useEffect(() => {
    if (!baseScene) return;

    setVisibleByPrefix(baseScene, "GARMENT_TOP__", snap.appearance?.top);
    setVisibleByPrefix(baseScene, "GARMENT_BOTTOM__", snap.appearance?.bottom);
    setVisibleByPrefix(baseScene, "HAIR__", snap.appearance?.hair);

    console.log("[garments] applied", snap.appearance);
  }, [baseScene, snap.appearance?.top, snap.appearance?.bottom, snap.appearance?.hair]);

  const hairMatsRef = useRef([]);

  const baseGroupRef = useRef();
  const dragPreviewMeshRef = useRef(null);
  const dragPreviewMatRef = useRef(null);
  const dragPreviewTexRef = useRef(null);
  const dragPreviewUrlRef = useRef(null);
  const tmpV3Ref = useRef(new THREE.Vector3());
  const overlayGroupRef = useRef();
  const paintGroupRef = useRef();

  const overlayMatsRef = useRef([]);
  const paintMatsRef = useRef([]);
  const currentOverlayTexRef = useRef(null);

  const lastMorphHashRef = React.useRef([]);

  const topGarmentMatsRef = useRef([]);
  const bottomGarmentMatsRef = useRef([]);

  // ✅ base AO from GLB + last stacked AO texture
  const baseAoTexRef = useRef(null); // THREE.Texture (original from GLB)
  const stackedAoTexRef = useRef(null); // THREE.Texture (canvas-made)
  const lastAoKeyRef = useRef(""); // string key to avoid rebuilding

  const hairAoTexRef = useRef(null);
  const lastHairAoKeyRef = useRef("");

  // baked white-ink overlay group
  const whiteOverlayGroupRef = useRef();
  const whiteOverlayMatsRef = useRef([]);
  const currentWhiteOverlayTexRef = useRef(null);
  const whiteOverlayMeshesRef = useRef([]);

  const underlayGroupRef = useRef();
  const underlayMatsRef = useRef([]);
  const currentUnderlayTexRef = useRef(null);

  // arrays of meshes in consistent traversal order
  const baseMeshesRef = useRef([]);
  const overlayMeshesRef = useRef([]);
  const paintMeshesRef = useRef([]);

  const underlayMeshesRef = useRef([]);

  const POSE_PREFIX = "POSE__";
  const POSE_RE = /^POSE__.+/i; // ✅ matches your state scheme

  function findMeshByName(scene, testFn) {
    let found = null;
    scene.traverse((o) => {
      if (found) return;
      if (!o?.isMesh && !o?.isSkinnedMesh) return;
      if (testFn(o.name || "")) found = o;
    });
    return found;
  }

  function findBodyMeshes(scene) {
    const name = (s) => (s || "").toLowerCase();

    const maleProxy =
      findMeshByName(
        scene,
        (n) => name(n).includes("male") && name(n).includes("paintproxy")
      ) ||
      findMeshByName(scene, (n) => name(n) === "malepaintproxy") ||
      findMeshByName(scene, (n) => name(n) === "male"); // last resort

    const femaleProxy =
      findMeshByName(
        scene,
        (n) => name(n).includes("female") && name(n).includes("paintproxy")
      ) ||
      findMeshByName(scene, (n) => name(n) === "femalepaintproxy") ||
      findMeshByName(scene, (n) => name(n) === "female"); // last resort

    const maleHR =
      findMeshByName(
        scene,
        (n) => name(n).includes("male") && name(n).includes("hr")
      ) || findMeshByName(scene, (n) => name(n) === "malehr");

    const femaleHR =
      findMeshByName(
        scene,
        (n) => name(n).includes("female") && name(n).includes("hr")
      ) || findMeshByName(scene, (n) => name(n) === "femalehr");

    return { maleProxy, femaleProxy, maleHR, femaleHR };
  }

  // after your scene is ready:
  const meshes = findBodyMeshes(
    baseScene /* or the scene you actually raycast */
  );
  window.__tt_body_meshes = meshes;

  // optional: keep your existing single-mesh latch too
  window.__tt_decal_pick_mesh =
    meshes.maleProxy ||
    meshes.femaleProxy ||
    meshes.maleHR ||
    meshes.femaleHR ||
    null;

  function clamp01(v) {
    v = Number(v);
    return Number.isFinite(v) ? Math.max(0, Math.min(1, v)) : 0;
  }
  const poseNamesRef = useRef([]); // discovered pose morph names (strings)

  function isDraggingNow() {
    const d = window.__tt_decal_drag;
    const active = !!(d?.active && d?.layerId);

    const now = performance.now();

    if (active) {
      lastDragSeenAtRef.current = now;
      // keep low-res for a beat AFTER release (prevents flicker)
      dragLowResUntilRef.current = now + 180;
      return true;
    }

    // if we stopped receiving active, stay low-res briefly
    return now < (dragLowResUntilRef.current || 0);
  }

  // --- garment toggles (Step 1) ---
  function setVisibleByPrefix(scene, prefix, activeKey) {
    if (!scene) return;

    scene.traverse((obj) => {
      if (!obj.isObject3D) return;

      const name = obj.name || "";

      // Only touch nodes that belong to this category
      if (!name.startsWith(prefix)) return;

      // If activeKey is "none", hide all in this category
      if (!activeKey || activeKey === "none") {
        obj.visible = false;
        return;
      }

      // Show only the one that matches the suffix
      // e.g. "GARMENT_TOP__bikini_top" matches activeKey "bikini_top"
      const suffix = name.slice(prefix.length);
      obj.visible = suffix === activeKey;
    });
  }

  React.useEffect(() => {
    if (!scene) return;

    const top = snap.appearance?.top ?? "none";
    const bottom = snap.appearance?.bottom ?? "none";
    const hair = snap.appearance?.hair ?? "none";

    setVisibleByPrefix(scene, "GARMENT_TOP__", top);
    setVisibleByPrefix(scene, "GARMENT_BOTTOM__", bottom);
    setVisibleByPrefix(scene, "HAIR__", hair);

    console.log("[garments] applied", { top, bottom, hair });
  }, [
    scene,
    snap.appearance?.top,
    snap.appearance?.bottom,
    snap.appearance?.hair,
  ]);

  function discoverPoseMorphNames() {
    const set = new Set();

    for (const mesh of baseMeshesRef.current) {
      const dict = mesh?.morphTargetDictionary;
      const inf = mesh?.morphTargetInfluences;
      if (!dict || !inf) continue;

      for (const name of Object.keys(dict)) {
        if (POSE_RE.test(name)) set.add(name);
      }
    }

    const names = Array.from(set).sort((a, b) => a.localeCompare(b));
    poseNamesRef.current = names;

    try {
      typeof onPoseMorphNames === "function" && onPoseMorphNames(names);
    } catch {}

    return names;
  }

  function applyPoseWeightsInternal(weights) {
    const names = poseNamesRef.current?.length
      ? poseNamesRef.current
      : discoverPoseMorphNames();

    if (!names?.length) return;

    for (const mesh of baseMeshesRef.current) {
      const dict = mesh?.morphTargetDictionary;
      const inf = mesh?.morphTargetInfluences;
      if (!dict || !inf) continue;

      // ✅ clear all pose morphs first (prevents stickiness)
      for (const n of names) {
        const idx = dict[n];
        if (idx === undefined) continue;
        inf[idx] = 0;
      }

      // ✅ apply new pose weights
      for (const [n, w] of Object.entries(weights || {})) {
        if (!POSE_RE.test(n)) continue;
        const idx = dict[n];
        if (idx === undefined) continue;
        inf[idx] = clamp01(w);
      }

      (Array.isArray(mesh.material) ? mesh.material : [mesh.material]).forEach(
        (m) => m && (m.needsUpdate = true)
      );
    }
  }

  // keep refs to the base skin materials so we can recolor without replacing
  const baseSkinMatsRef = useRef([]);

  const aoProcessedScenesRef = useRef(new WeakSet());

  const painterRef = useRef(null);
  const bodyMeshRef = useRef(null);

  function isGarmentLike(n) {
    const role = (n?.userData?.ttRole || "").toLowerCase();
    if (role === "garment" || role === "accessory" || role === "hair")
      return true;

    const nm = (n?.name || "").toLowerCase();
    return /short|shorts|bikini|bra|panty|pants|garment|cloth|outfit|shoe|top|bottom|hat|hair|sock/i.test(
      nm
    );
  }
  const tmpBox = useMemo(() => new THREE.Box3(), []);
  const tmpSize = useMemo(() => new THREE.Vector3(), []);
  const tmpCenter = useMemo(() => new THREE.Vector3(), []);

  function groundAndCenter() {
    const root = rootRef.current;
    const baseGroup = baseGroupRef.current;
    if (!root || !baseGroup) return;

    baseGroup.updateMatrixWorld(true);

    tmpBox.setFromObject(baseGroup);
    tmpBox.getSize(tmpSize);

    const h = Number.isFinite(tmpSize.y) && tmpSize.y > 1e-6 ? tmpSize.y : 1;
    const s = targetHeight / h;

    root.scale.setScalar(s);

    // ground bottom to y=0
    root.position.y = -tmpBox.min.y * s;

    // optional centering
    tmpBox.getCenter(tmpCenter);
    root.position.x = -tmpCenter.x * s;
    root.position.z = -tmpCenter.z * s;

    root.updateWorldMatrix(true, true);

    console.log("[groundAndCenter]", {
      minY: tmpBox.min.y,
      scale: s,
      rootY: root.position.y,
    });
  }

  function applyClothingVisibility(root, appearance) {
    if (!root) return;

    const top = appearance?.top ?? "none";
    const bottom = appearance?.bottom ?? "none";

    root.traverse((obj) => {
      const n = obj.name || "";

      // ----- TOP -----
      if (n === "UNDERWEAR_TOP") {
        obj.visible = top === "none";
        return;
      }
      if (n.startsWith("GARMENT_TOP__")) {
        obj.visible = top !== "none" && n.endsWith(top);
        return;
      }

      // ----- BOTTOM -----
      if (n === "UNDERWEAR_BOTTOM") {
        obj.visible = bottom === "none";
        return;
      }
      if (n.startsWith("GARMENT_BOTTOM__")) {
        obj.visible = bottom !== "none" && n.endsWith(bottom);
        return;
      }
    });
  }

  useEffect(() => {
    if (!baseScene) return;
  
    const sex = (contextSex || snap?.sex || "female") === "male" ? "male" : "female";
    const hairKey = snap?.appearance?.hair || "none";
  
    // no hair selected
    if (!hairKey || hairKey === "none") {
      // optional: remove previous aoMap from hair mats
      baseScene.traverse((obj) => {
        const name = obj?.name || "";
        if (!obj?.isMesh) return;
        if (!name.startsWith("HAIR__")) return;
  
        const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
        mats.forEach((m) => {
          if (!m || !m.isMaterial) return;
          if (m.aoMap) {
            m.aoMap = null;
            m.needsUpdate = true;
          }
        });
      });
      lastHairAoKeyRef.current = "";
      return;
    }
  
    // build URL
    const hairUrl = hairAoPathFor({ sex, appearance: snap.appearance });
    if (!hairUrl) return;
  
    // avoid reloading same file
    const key = `${sex}:${hairKey}:${hairUrl}`;
    if (lastHairAoKeyRef.current === key && hairAoTexRef.current) {
      // already loaded, just re-apply (in case materials were recreated)
    } else {
      lastHairAoKeyRef.current = key;
  
      // dispose old
      try {
        hairAoTexRef.current?.dispose?.();
      } catch {}
      hairAoTexRef.current = null;
    }
  
    let cancelled = false;
  
    (async () => {
      let img;
      try {
        img = await loadImage(hairUrl);
      } catch (e) {
        console.warn("[hairAO] failed to load", hairUrl, e);
        return;
      }
      if (cancelled) return;
  
      // turn image into texture (AO is linear!)
      const c = document.createElement("canvas");
      c.width = img.naturalWidth || img.width || 512;
      c.height = img.naturalHeight || img.height || 512;
      const ctx = c.getContext("2d");
      ctx.drawImage(img, 0, 0, c.width, c.height);
  
      const tex = new THREE.CanvasTexture(c);
      tex.flipY = false;
      tex.colorSpace = THREE.NoColorSpace;
      tex.needsUpdate = true;
  
      hairAoTexRef.current = tex;
  
      // apply to hair meshes ONLY
      baseScene.traverse((obj) => {
        const name = obj?.name || "";
        if (!obj?.isMesh) return;
        if (!name.startsWith("HAIR__")) return;
  
        // IMPORTANT: aoMap needs uv2. If you don't have uv2, copy uv -> uv2.
        const g = obj.geometry;
        if (g?.attributes?.uv && !g?.attributes?.uv2) {
          g.setAttribute("uv2", g.attributes.uv);
          g.attributes.uv2.needsUpdate = true;
        }
  
        const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
        mats.forEach((m) => {
          if (!m || !m.isMaterial) return;
  
          m.aoMap = tex;
          m.aoMapIntensity = 1.25; // tweak 0.8–2.0
          m.needsUpdate = true;
        });
      });
  
      console.log("[hairAO] applied", { hairKey, hairUrl });
    })();
  
    return () => {
      cancelled = true;
    };
  }, [baseScene, snap?.appearance?.hair, contextSex]);

  /* ---------- expose imperative API ---------- */
  useImperativeHandle(
    ref,
    () => ({
      getObject: () => rootRef.current,
      getMorphTargets: () => {
        const out = [];
        for (const m of baseMeshesRef.current) {
          if (!m?.morphTargetDictionary || !m?.morphTargetInfluences) continue;
          const entries = Object.entries(m.morphTargetDictionary).map(
            ([name, index]) => ({ name, index })
          );
          if (entries.length) {
            out.push({ id: m.uuid, name: m.name || "Mesh", mesh: m, entries });
          }
        }
        return out;
      },
      syncMorphsNow: () => {
        const A = baseMeshesRef.current;
        const B = overlayMeshesRef.current;
        const C = paintMeshesRef.current;
        const len = Math.min(A.length, B.length, C.length);
        for (let i = 0; i < len; i++) {
          const a = A[i],
            b = B[i],
            c = C[i];
          const inf = a?.morphTargetInfluences;
          if (!inf) continue;

          if (
            b?.morphTargetInfluences &&
            b.morphTargetInfluences.length === inf.length
          ) {
            for (let k = 0; k < inf.length; k++)
              b.morphTargetInfluences[k] = inf[k];
            (Array.isArray(b.material) ? b.material : [b.material]).forEach(
              (m) => m && (m.needsUpdate = true)
            );
          }

          if (
            c?.morphTargetInfluences &&
            c.morphTargetInfluences.length === inf.length
          ) {
            for (let k = 0; k < inf.length; k++)
              c.morphTargetInfluences[k] = inf[k];
            (Array.isArray(c.material) ? c.material : [c.material]).forEach(
              (m) => m && (m.needsUpdate = true)
            );
          }
        }
      },
      getPoseMorphTargets: () => {
        const out = [];
        for (const m of baseMeshesRef.current) {
          if (!m?.morphTargetDictionary || !m?.morphTargetInfluences) continue;

          const entries = Object.entries(m.morphTargetDictionary)
            .map(([name, index]) => ({ name, index }))
            .filter((e) => /^POSE__/i.test(e.name));

          if (entries.length) {
            out.push({ id: m.uuid, name: m.name || "Mesh", mesh: m, entries });
          }
        }
        return out;
      },

      applyPoseWeights: (poseWeights) => {
        // poseWeights = { "POSE_HandsOnHip": 1, "POSE_ArmsUp": 0, ... }

        for (const mesh of baseMeshesRef.current) {
          const dict = mesh?.morphTargetDictionary;
          const inf = mesh?.morphTargetInfluences;
          if (!dict || !inf) continue;

          for (const [poseName, w] of Object.entries(poseWeights || {})) {
            const idx = dict[poseName];
            if (idx === undefined) continue;
            inf[idx] = w;
          }
        }
      },
    }),

    []
  );

  function groundRootToY0(root) {
    if (!root) return;

    // compute bbox in root space
    const box = new THREE.Box3().setFromObject(root);
    if (!isFinite(box.min.y) || !isFinite(box.max.y)) return;

    // shift so minY sits at 0
    const dy = box.min.y;
    root.position.y -= dy;

    root.updateWorldMatrix(true, true);

    console.log("[ground] grounded root by", dy, "new rootY", root.position.y);
  }

  /* ---------- assign skin materials to BASE (once per baseScene) ---------- */
  useEffect(() => {
    if (!baseScene) return;

    // ✅ HARD GUARD: only process this scene once
    if (aoProcessedScenesRef.current.has(baseScene)) {
      // console.log("[AO] baseScene already processed, skipping", baseScene.uuid);
      return;
    }
    aoProcessedScenesRef.current.add(baseScene);

    // ✅ optional: prove whether nodeNameRegex identity is changing
    // console.log("[AO] processing baseScene", baseScene.uuid, "regex", nodeNameRegex);

    const skinCol = new THREE.Color(skinColor);
    const dummyMap = makeWhiteTex();

    // ✅ clear refs so they don't grow if something ever reprocesses
    baseSkinMatsRef.current = [];
    hairMatsRef.current = [];
    baseMeshesRef.current = [];

    const mode = snap.appearance?.hairColorMode ?? "preset";
    const presetId = snap.appearance?.hairColorPreset ?? "dark";
    const customHex = snap.appearance?.hairColorCustom ?? "#2b1a12";

    const resolveHairHex = () => {
      if (mode === "skin") return skinColor; // your existing value
      if (mode === "custom") return customHex;
      return HAIR_PRESET_MAP[presetId] ?? HAIR_PRESET_MAP.dark;
    };

    const hairHex = resolveHairHex();

    baseScene.traverse((obj) => {
      console.log("[hair] collected mats", hairMatsRef.current?.length || 0);
      if (!obj.isMesh) return;

      // --- KTX2 DEBUG (place HERE: top of baseScene.traverse, before you touch materials) ---
      if (!__ktxSeen.has(obj.name)) {
        __ktxSeen.add(obj.name);

        const mats = Array.isArray(obj.material)
          ? obj.material
          : [obj.material];
        for (const m of mats) {
          if (!m) continue;

          if (m.map) {
            console.log(
              "[KTX2 map]",
              obj.name,
              !!m.map.isCompressedTexture,
              m.map
            );
          }
          if (m.normalMap) {
            console.log(
              "[KTX2 normal]",
              obj.name,
              !!m.normalMap.isCompressedTexture,
              m.normalMap
            );
          }
        }
      }
      // --- end KTX2 DEBUG ---

      // garments should cast shadows
      if (obj.name.startsWith("GARMENT_") || obj.name.startsWith("HAIR_")) {
        obj.castShadow = true;
        obj.receiveShadow = false;
      }

      const name = String(obj.name || "");
      const isTop = name.startsWith("GARMENT_TOP__");
      const isBottom = name.startsWith("GARMENT_BOTTOM__");

      if (obj.isMesh && (isTop || isBottom)) {
        const mats = Array.isArray(obj.material)
          ? obj.material
          : [obj.material];

        // store refs so we can recolor fast later
        (isTop ? topGarmentMatsRef.current : bottomGarmentMatsRef.current).push(
          ...mats
        );

        // optional: ensure they have a color property
        mats.forEach((m) => {
          if (m && m.color) m.needsUpdate = true;
        });
      }

      // body should receive shadows
      if (obj.name.startsWith("BODY") || obj.name.startsWith("SKIN")) {
        obj.receiveShadow = true;
        obj.castShadow = false;
      }

      // tag proxy once, early
      if (/PaintProxy/i.test(obj.name)) {
        obj.userData = obj.userData || {};
        obj.userData.ttRole = "paintProxy";
        obj.userData.ttSkipOverlay = true;
        obj.visible = false;
      }

      const ud = obj.userData || {};
      const kind = String(ud.tt_kind || ud.ttKind || "").toLowerCase();

      // tag hair by name (GLB naming)
      if (obj.isMesh && name.startsWith("HAIR_")) {
        obj.userData = obj.userData || {};
        obj.userData.ttRole = "hair";
      }

      // hair
      // tag hair by name (GLB naming)
      if (obj.isMesh && name.startsWith("HAIR_")) {
        obj.userData = obj.userData || {};
        obj.userData.ttRole = "hair";
      }

      // hair (TEXTURE ROOTS + NORMALS)
      const role = String(obj.userData?.ttRole || "").toLowerCase();
      const isHair = role === "hair" || name.startsWith("HAIR_");
      if (isHair) {
        const hairHex = resolveHairHex(snap);
        const hairCol = new THREE.Color(hairHex);

        const mats = Array.isArray(obj.material)
          ? obj.material
          : [obj.material];

        for (const m of mats) {
          if (!m || !m.isMaterial) continue;

          // --- debug what material we actually have ---
          console.log(
            "[hair mat]",
            obj.name,
            m.type,
            "hasNormal:",
            !!m.normalMap,
            "normalScale:",
            !!m.normalScale
          );

          // ✅ recolor
          if (m.color) m.color.copy(hairCol);

          // ✅ sheen-ish baseline (tweak to taste)
          m.roughness = 0.6; // lower = shinier
          m.metalness = 0.0;
          m.envMapIntensity = 0.25; // bump for sheen
          m.side = THREE.FrontSide;
          m.depthTest = true;
          m.depthWrite = true;
          m.toneMapped = true;

          // ✅ NORMAL MAP: force correct settings + strength
          if (m.normalMap) {
            m.normalMap.colorSpace = THREE.NoColorSpace;
            m.normalMap.flipY = false;

            if (m.normalScale) {
              // start here; if still subtle go 3.0 / 4.0
              m.normalScale.set(2.5, 2.5);
            }
          }

          // ✅ track for later recolor pass (fix your “mats: 2” issue)
          m.userData = m.userData || {};
          if (!m.userData.ttHairTracked) {
            m.userData.ttHairTracked = true;
            hairMatsRef.current.push(m);
          }

          m.needsUpdate = true;
        }

        obj.userData = obj.userData || {};
        obj.userData.ttRole = "hair";
        obj.receiveShadow = true;

        // sanity: hair geometry attrs
        console.log(
          "[hair attrs]",
          obj.name,
          "uv:",
          !!obj.geometry?.attributes?.uv,
          "uv2:",
          !!obj.geometry?.attributes?.uv2,
          "normal:",
          !!obj.geometry?.attributes?.normal
        );

        return;
      }

      // garments/accessories skip skin logic
      if (isGarmentLike(obj) || kind === "garment") return;

      const looks =
        nodeNameRegex?.test?.(obj.name) ||
        (Array.isArray(obj.material)
          ? obj.material.some((m) => nodeNameRegex?.test?.(m?.name ?? ""))
          : nodeNameRegex?.test?.(obj.material?.name ?? ""));

      if (!looks) return;

      // ✅ prevent double-processing on same mesh
      // (extra safety if this scene ever gets re-traversed)
      if (obj.userData?.__tt_skinMatApplied) return;
      obj.userData.__tt_skinMatApplied = true;

      const srcMats = Array.isArray(obj.material)
        ? obj.material
        : [obj.material];

      const copyTex = (tex, { srgb = false } = {}) => {
        if (!tex) return null;
        tex.flipY = false;
        tex.colorSpace = srgb ? THREE.SRGBColorSpace : THREE.NoColorSpace;
        tex.needsUpdate = true;
        return tex;
      };

      const newMats = srcMats.map((src) => {
        const m = new THREE.MeshStandardMaterial({
          color: skinCol,
          roughness: skinRoughness,
          metalness: skinMetalness,
          side: THREE.FrontSide,
        });

        m.map = copyTex(src?.map || dummyMap, { srgb: true });
        m.normalMap = copyTex(src?.normalMap, { srgb: false });
        m.roughnessMap = copyTex(src?.roughnessMap, { srgb: false });
        m.metalnessMap = copyTex(src?.metalnessMap, { srgb: false });
        m.aoMap = copyTex(src?.aoMap, { srgb: false });

        // ✅ capture the original GLB AO once
        if (!baseAoTexRef.current && m.aoMap) {
          baseAoTexRef.current = m.aoMap;
        }

        if (m.aoMap) {
          m.aoMapIntensity = 3;
          m.roughness = Math.min(1, m.roughness + 0.03);
        }

        if (m.normalMap && m.normalScale)
          m.normalScale.setScalar(skinNormalScale);

        m.envMapIntensity = skinEnvIntensity;
        m.toneMapped = true;
        m.depthTest = true;
        m.depthWrite = true;
        m.needsUpdate = true;

        baseSkinMatsRef.current.push(m);

        // ✅ debug: this should now only print ONCE per load
        // console.log("[AO-PUSH]", obj.name, { isSkinnedMesh: !!obj.isSkinnedMesh });
        // console.count("[AO] push run");

        return m;
      });

      obj.material = Array.isArray(obj.material) ? newMats : newMats[0];
      obj.receiveShadow = true;
      obj.castShadow = true;

      baseMeshesRef.current.push(obj);
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseScene, nodeNameRegex]);

  useEffect(() => {
    const hex = resolveHairHex({
      appearance: snap.appearance,
      skinColor: snap.skinColor,
    });

    const c = new THREE.Color(hex);

    // Always traverse the live baseScene to find hair materials, instead of
    // relying on hairMatsRef populated by the once-per-baseScene AO effect.
    //
    // Why no cache: the AO traversal is gated by an aoProcessedScenesRef
    // WeakSet guard, and under session-reset / dashboard-remount sequences
    // the recolor effect could fire before the AO traversal had populated
    // the ref — leaving the picker silently driving an empty array. Color
    // changes are user-driven (rare) and a tree walk over the rigged body
    // is cheap, so always re-collect. We still update hairMatsRef for any
    // other code that reads it. The ttHairTracked flag prevents the AO
    // traversal (when it eventually runs) from double-pushing.
    const liveMats = [];
    if (baseScene) {
      baseScene.traverse((obj) => {
        if (!obj.isMesh) return;
        const objName = String(obj.name || "");
        const role = String(obj.userData?.ttRole || "").toLowerCase();
        if (role !== "hair" && !objName.startsWith("HAIR_")) return;
        const objMats = Array.isArray(obj.material) ? obj.material : [obj.material];
        for (const m of objMats) {
          if (!m || !m.isMaterial) continue;
          liveMats.push(m);
          m.userData = m.userData || {};
          m.userData.ttHairTracked = true;
        }
      });
    }
    hairMatsRef.current = liveMats;

    console.log("[hair] recolor", { hex, mats: liveMats.length });

    for (const m of liveMats) {
      if (!m?.color) continue;
      m.color.copy(c);
      m.needsUpdate = true;
    }
  }, [
    snap.skinColor,
    snap.appearance?.hairColorMode,
    snap.appearance?.hairColorPreset,
    snap.appearance?.hairColorCustom,
    snap.appearance?.hair, // re-collect when hair STYLE changes too — different mesh, different mats
    baseScene,
  ]);

  useEffect(() => {
    const a = snap.appearance || {};
    const hex = resolveGarmentHex(
      a.topColorMode,
      a.topColorPreset,
      a.topColorCustom
    );
    const c = new THREE.Color(hex);

    for (const m of topGarmentMatsRef.current || []) {
      if (!m?.color) continue;
      m.color.copy(c);
      m.needsUpdate = true;
    }
  }, [snap.appearance?.topColorMode, snap.appearance?.topColorPreset, snap.appearance?.topColorCustom]);

  useEffect(() => {
    const a = snap.appearance || {};
    const hex = resolveGarmentHex(
      a.bottomColorMode,
      a.bottomColorPreset,
      a.bottomColorCustom
    );
    const c = new THREE.Color(hex);

    for (const m of bottomGarmentMatsRef.current || []) {
      if (!m?.color) continue;
      m.color.copy(c);
      m.needsUpdate = true;
    }
  }, [snap.appearance?.bottomColorMode, snap.appearance?.bottomColorPreset, snap.appearance?.bottomColorCustom]);

  /* ---------- only update skin COLOR on skinColor change ---------- */
  useEffect(() => {
    const col = new THREE.Color(skinColor);
    for (const m of baseSkinMatsRef.current) {
      if (m && m.color) {
        m.color.copy(col);
        m.needsUpdate = true;
      }
    }
  }, [skinColor]);

  // (Removed: duplicate hair-recolor useEffect that did the same work as the
  // one above with the same dependency array — pure redundancy. The remaining
  // effect is now self-healing for the empty-mats race condition.)

  useEffect(() => {
    for (const m of baseSkinMatsRef.current) {
      if (!m) continue;
      if ("roughness" in m) m.roughness = skinRoughness;
      if ("metalness" in m) m.metalness = skinMetalness;
      if ("envMapIntensity" in m) m.envMapIntensity = skinEnvIntensity;

      if (m.normalMap && m.normalScale) {
        m.normalScale.setScalar(skinNormalScale);
      }
      m.needsUpdate = true;
    }
  }, [skinRoughness, skinMetalness, skinEnvIntensity, skinNormalScale]);

  /* ---------- build groups (overlay / live paint) ---------- */
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    root.clear();

    // BASE
    const baseGroup = new THREE.Group();
    baseGroup.add(baseScene);
    root.add(baseGroup);
    baseGroupRef.current = baseGroup;

    // reset transforms (clear() does not reset transforms)
    root.position.set(0, 0, 0);
    root.rotation.set(0, 0, 0);
    root.scale.set(1, 1, 1);
    root.updateWorldMatrix(true, true);

    // stabilize bind pose before measuring
    forceRestPose(baseScene);
    baseGroup.updateWorldMatrix(true, true);

    // ✅ collect base meshes (needed for morphs + pose morphs)
    baseMeshesRef.current = [];
    baseScene.traverse((n) => {
      if (n.isMesh) baseMeshesRef.current.push(n);
    });

    // measure at scale = 1
    const box1 = new THREE.Box3().setFromObject(baseGroup);
    const size1 = new THREE.Vector3();
    box1.getSize(size1);

    const height = Number.isFinite(size1.y) && size1.y > 1e-6 ? size1.y : 1;
    const scale = targetHeight / height;

    // APPLY SCALE
    root.scale.setScalar(scale);
    root.updateWorldMatrix(true, true);
    baseGroup.updateWorldMatrix(true, true);

    // measure AFTER scale (so minY is already in final units)
    const box2 = new THREE.Box3().setFromObject(baseGroup);
    const center2 = new THREE.Vector3();
    box2.getCenter(center2);

    // stick feet to floor (y = 0), then apply your art offset
    const groundDy = -box2.min.y; // this is the exact "bring minY to 0"
    const artDy = yOffset || 0; // your additional tweak

    // stick feet to floor (y = 0) — HARD FORCE
    root.position.y = -box2.min.y;
    console.log("[ground] HARD", { minY: box2.min.y, rootY: root.position.y });

    console.log("[ground] APPLY", {
      minY: box2.min.y,
      groundDy,
      artDy,
      rootY: root.position.y,
    });

    // center X/Z so framing matches across models
    root.position.x = -center2.x;
    root.position.z = -center2.z;

    root.updateWorldMatrix(true, true);

    console.log("[ground]", {
      height,
      scale,
      minY: box2.min.y,
      rootY: root.position.y,
    });

    // measure AFTER scale
    baseGroup.updateWorldMatrix(true, true);

    const bodyBox = computeMinYFromMeshes(baseGroup);
    if (bodyBox) {
      root.position.y = -bodyBox.min.y + yOffset; // yOffset usually 0
    } else {
      // fallback (if naming doesn't match for some reason)
      const box2 = new THREE.Box3().setFromObject(baseGroup);
      // box2 is WORLD-space bounds (setFromObject uses matrixWorld)
      const groundDyWorld = -box2.min.y + yOffset; // move up so minY becomes 0 (+ art offset)

      // Apply that delta in WORLD space, then convert back to root's parent local space
      const parent = root.parent;
      if (parent) {
        const rootWorld = new THREE.Vector3();
        root.getWorldPosition(rootWorld);

        const desiredWorld = rootWorld
          .clone()
          .add(new THREE.Vector3(0, groundDyWorld, 0));

        // convert desired world position into parent-local position
        parent.worldToLocal(desiredWorld);

        root.position.copy(desiredWorld);
      } else {
        // fallback (rare)
        root.position.y += groundDyWorld;
      }

      root.updateWorldMatrix(true, true);
    }

    // OVERLAY (Multiply)
    const overlay = baseScene.clone(true);
    overlay.userData.ttOverlay = true;
    root.add(overlay);
    overlayGroupRef.current = overlay;

    disableRaycast(overlay);

    overlayMatsRef.current = [];
    overlay.traverse((n) => {
      if (!n.isMesh) return;
      if (isPaintProxy(n)) {
        n.visible = false;
        return;
      }
      if (isGarmentLike(n)) {
        n.visible = false;
        return;
      }

      const m = new THREE.MeshStandardMaterial({
        color: 0xffffff,
        transparent: true,
        opacity: 1.0,
        alphaTest: 0.01,
        depthTest: true,
        depthWrite: false,
        side: THREE.FrontSide,
        toneMapped: false,
        blending: THREE.NormalBlending,
      });

      m.polygonOffset = true;
      m.polygonOffsetFactor = -1;
      m.polygonOffsetUnits = -1;

      n.material = m;
      n.renderOrder = 1000;
      overlayMatsRef.current.push(m);
    });
    overlay.visible = false;

    // WHITE overlay
    const whiteOverlay = baseScene.clone(true);
    whiteOverlay.userData.ttWhiteOverlay = true;
    root.add(whiteOverlay);
    whiteOverlayGroupRef.current = whiteOverlay;

    disableRaycast(whiteOverlay);

    whiteOverlayMatsRef.current = [];
    whiteOverlay.traverse((n) => {
      if (!n.isMesh) return;
      if (isPaintProxy(n)) {
        n.visible = false;
        return;
      }
      if (isGarmentLike(n)) {
        n.visible = false;
        return;
      }

      const m = new THREE.MeshBasicMaterial({
        color: 0xffffff,
        transparent: true,
        opacity: 0.0,
        alphaTest: 0.01,
        depthTest: true,
        depthWrite: false,
        side: THREE.FrontSide,
        blending: THREE.AdditiveBlending,
        toneMapped: false,
      });

      m.polygonOffset = true;
      m.polygonOffsetFactor = -1;
      m.polygonOffsetUnits = -1;

      n.material = m;
      n.renderOrder = 1001;
      whiteOverlayMatsRef.current.push(m);
    });
    whiteOverlay.visible = false;

    // UNDERLAY GROUP (disabled for now)
    const underlayOverlay = baseScene.clone(true);
    underlayOverlay.userData.ttUnderlay = true;
    root.add(underlayOverlay);
    underlayGroupRef.current = underlayOverlay;

    disableRaycast(underlayOverlay);

    underlayMatsRef.current = [];
    underlayOverlay.traverse((n) => {
      if (!n.isMesh) return;
      if (isPaintProxy(n)) {
        n.visible = false;
        return;
      }
      if (isGarmentLike(n)) {
        n.visible = false;
        return;
      }

      const m = new THREE.MeshStandardMaterial({
        color: 0xffffff,
        metalness: 0,
        roughness: 0.6,
        transparent: true,
        opacity: 1.0,
        alphaTest: 0.02,
        depthTest: true,
        depthWrite: false,
        side: THREE.FrontSide,
        toneMapped: false,
        blending: THREE.NormalBlending, // underlay is just a normal overlay
      });

      m.morphTargets = true;
      m.morphNormals = true;

      m.polygonOffset = true;
      m.polygonOffsetFactor = -5; // slightly behind ink overlay
      m.polygonOffsetUnits = -5;

      n.material = m;
      n.renderOrder = 950; // behind ink (1000) but above base
      n.frustumCulled = false;
      underlayMatsRef.current.push(m);
    });

    underlayOverlay.visible = false; // IMPORTANT: keep disabled

    try {
      dragPreviewTexHiRef.current?.dispose?.();
    } catch {}
    try {
      dragPreviewTexLoRef.current?.dispose?.();
    } catch {}
    dragPreviewTexHiRef.current = null;
    dragPreviewTexLoRef.current = null;
    dragPreviewUrlHiRef.current = null;
    dragPreviewUrlLoRef.current = null;

    // LIVE PAINT overlay
    const paintOverlay = baseScene.clone(true);
    paintOverlay.userData.ttPaintOverlay = true;
    root.add(paintOverlay);
    paintGroupRef.current = paintOverlay;

    disableRaycast(paintOverlay);

    paintMatsRef.current = [];
    paintOverlay.traverse((n) => {
      if (!n.isMesh) return;
      if (isPaintProxy(n)) {
        n.visible = false;
        return;
      }
      if (isGarmentLike(n)) {
        n.visible = false;
        return;
      }

      const m = new THREE.MeshBasicMaterial({
        color: 0xffffff,
        transparent: true,
        opacity: 0.0,
        alphaTest: 0.01,
        depthTest: true,
        depthWrite: false,
        side: THREE.FrontSide,
        blending: THREE.NormalBlending,
        toneMapped: false,
      });

      m.polygonOffset = true;
      m.polygonOffsetFactor = -1;
      m.polygonOffsetUnits = -1;

      n.material = m;
      n.renderOrder = 1001;
      paintMatsRef.current.push(m);
    });
    paintOverlay.visible = false;

    whiteOverlayMeshesRef.current = [];
    whiteOverlay.traverse((wn) => {
      if (wn.isMesh) whiteOverlayMeshesRef.current.push(wn);
    });

    // --- GPU DRAG PREVIEW (MUST be created AFTER root.clear()) ---
    try {
      // remove old preview on hot reload / rebuild
      if (dragPreviewMeshRef.current?.parent) {
        dragPreviewMeshRef.current.parent.remove(dragPreviewMeshRef.current);
      }

      // build new preview
      const geo = new THREE.PlaneGeometry(1, 1, 1, 1);
      const mat = new THREE.MeshBasicMaterial({
        transparent: true,
        opacity: 1.0,
        depthTest: false, // ✅ draw on top (prevents “disappears”)
        depthWrite: false,
        side: THREE.DoubleSide,
        toneMapped: false,
      });

      const mesh = new THREE.Mesh(geo, mat);
      mesh.renderOrder = 9999;
      mesh.visible = false;

      // attach under root so it inherits model transforms
      root.add(mesh);

      dragPreviewMeshRef.current = mesh;
      dragPreviewMatRef.current = mat;

      // clear old texture cache
      try {
        dragPreviewTexRef.current?.dispose?.();
      } catch {}
      dragPreviewTexRef.current = null;
      dragPreviewUrlRef.current = null;
    } catch (e) {
      console.warn("[dragPreview] init failed", e);
    }

    // collect meshes for morph syncing
    overlayMeshesRef.current = [];
    paintMeshesRef.current = [];
    overlay.traverse((n) => {
      if (n.isMesh) overlayMeshesRef.current.push(n);
    });
    paintOverlay.traverse((n) => {
      if (n.isMesh) paintMeshesRef.current.push(n);
    });

    underlayMeshesRef.current = [];
    underlayOverlay.traverse((n) => {
      if (n.isMesh) underlayMeshesRef.current.push(n);
    });

    // ✅ pick ALL paintable meshes as GuidePaint targets (fixes arm selection / touch misses)
    const targets = [];
    const candidates = []; // optional: keep if you still want "main body" ref

    baseScene.traverse((n) => {
      if (!n?.isMesh) return;

      // hide proxies, skip garments
      if (isPaintProxy(n)) {
        n.visible = false;
        return;
      }
      if (isGarmentLike(n)) return;

      const nm = (n.name || "").toLowerCase();
      if (/helper|guide|gizmo|axis|ring|button|inner|shell/i.test(nm)) return;

      targets.push(n);

      // optional: still compute "largest" for bodyMeshRef if you use it elsewhere
      const bb = new THREE.Box3().setFromObject(n);
      const s = new THREE.Vector3();
      bb.getSize(s);
      candidates.push({ n, area: s.x * s.y });
    });

    // keep your "main" body ref (optional)
    candidates.sort((a, b) => b.area - a.area);
    bodyMeshRef.current = candidates[0]?.n || null;

    // GuidePaint singleton
    if (!guidePaintRef.current || guidePaintRef.current.__ver !== 2) {
      const GP_SIZE = isIPad() ? 2048 : isProbablyMobile() ? 1024 : 2048;
      guidePaintRef.current = new GuidePaint({ size: GP_SIZE });
    }
    painterRef.current = guidePaintRef.current;

    try {
      painterRef.current.setTargets(targets, camera, gl.domElement);
      painterRef.current.setUvRect({ x: 0, y: 0, w: 1, h: 1 });
    } catch {}

    /// ✅ discover pose morph names + apply initial weights
    discoverPoseMorphNames();
    applyPoseWeightsInternal(poseWeights);

    try {
      onReady?.(rootRef.current);
      window.dispatchEvent(new CustomEvent("morph:collect"));
      window.dispatchEvent(new CustomEvent("morph:changed"));
    } catch {}

    setMatsReadyTick((t) => t + 1);

    return () => {
      if (currentOverlayTexRef.current) {
        currentOverlayTexRef.current.dispose?.();
        currentOverlayTexRef.current = null;
      }
      if (currentWhiteOverlayTexRef.current) {
        currentWhiteOverlayTexRef.current.dispose?.();
        currentWhiteOverlayTexRef.current = null;
      }
      overlayMatsRef.current.forEach((m) => m.map?.dispose?.());
      paintMatsRef.current.forEach((m) => m.map?.dispose?.());
      overlayMatsRef.current = [];
      paintMatsRef.current = [];
      baseMeshesRef.current = [];
      baseSkinMatsRef.current = [];
      whiteOverlayMeshesRef.current = [];
      // --- cleanup drag preview ---
      try {
        dragPreviewTexRef.current?.dispose?.();
        dragPreviewTexRef.current = null;
        dragPreviewUrlRef.current = null;

        if (dragPreviewMeshRef.current?.parent) {
          dragPreviewMeshRef.current.parent.remove(dragPreviewMeshRef.current);
        }
        dragPreviewMeshRef.current?.geometry?.dispose?.();
        dragPreviewMatRef.current?.dispose?.();

        dragPreviewMeshRef.current = null;
        dragPreviewMatRef.current = null;
      } catch {}
    };
  }, [baseScene, targetHeight, yOffset, edgeClip]);

  useEffect(() => {
    let cancelled = false;

    async function run() {
      const appearance = snap.appearance || {
        top: "none",
        bottom: "none",
        hair: "none",
      };

      const sex =
        (snap.sex || contextSex || "female") === "male" ? "male" : "female";
      const { top, bottom, femaleUnderwearBase } = aoPathForAppearance(
        appearance,
        sex // ✅ use the resolved sex
      );

      // ✅ include base override in the key so "none" can still swap bases
      const key = `${femaleUnderwearBase || "base_glb"}|${top || "none"}|${
        bottom || "none"
      }`;
      if (key === lastAoKeyRef.current) return;
      lastAoKeyRef.current = key;

      // GLB embedded AO (the one you currently capture at material build time)
      const glbBaseAo = baseAoTexRef.current;
      if (!glbBaseAo) return;

      // base override URL: only for female when top is none
      const baseOverrideUrl =
        sex === "female" && femaleUnderwearBase ? femaleUnderwearBase : null;

      // No garment AO selected -> just apply base AO (chosen above)
      if (!top && !bottom) {
        // if there's a baseOverrideUrl, build a texture from it; otherwise use GLB AO
        if (baseOverrideUrl) {
          try {
            const stacked = await buildStackedAoTexture({
              baseAoTex: glbBaseAo,
              baseOverrideUrl,
              topUrl: null,
              bottomUrl: null,
            });
            if (cancelled || !stacked) return;

            try {
              stackedAoTexRef.current?.dispose?.();
            } catch {}
            stackedAoTexRef.current = stacked;

            for (const m of baseSkinMatsRef.current) {
              if (!m) continue;
              m.aoMap = stacked;
              m.aoMapIntensity = 3;
              m.needsUpdate = true;
            }
          } catch (e) {
            console.warn("[ao] underwear base failed, using GLB AO", e);
            for (const m of baseSkinMatsRef.current) {
              if (!m) continue;
              m.aoMap = glbBaseAo;
              m.aoMapIntensity = 3;
              m.needsUpdate = true;
            }
          }
        } else {
          for (const m of baseSkinMatsRef.current) {
            if (!m) continue;
            m.aoMap = glbBaseAo;
            m.aoMapIntensity = 3;
            m.needsUpdate = true;
          }
        }
        return;
      }

      // ✅ stack garment AO(s) on chosen base
      try {
        const stacked = await buildStackedAoTexture({
          baseAoTex: glbBaseAo,
          baseOverrideUrl, // ✅ underwear when female + top none
          topUrl: top,
          bottomUrl: bottom,
        });

        if (cancelled || !stacked) return;

        try {
          stackedAoTexRef.current?.dispose?.();
        } catch {}
        stackedAoTexRef.current = stacked;

        for (const m of baseSkinMatsRef.current) {
          if (!m) continue;
          m.aoMap = stacked;
          m.aoMapIntensity = 3;
          m.needsUpdate = true;
        }
      } catch (e) {
        console.warn("[ao-stack] failed, falling back to base AO:", e);
        console.log("[ao] paths", {
          top,
          bottom,
          femaleUnderwearBase,
          sex: contextSex,
        });
        for (const m of baseSkinMatsRef.current) {
          if (!m) continue;
          m.aoMap = glbBaseAo;
          m.aoMapIntensity = 3;
          m.needsUpdate = true;
        }
      }
    }

    run();
    return () => {
      cancelled = true;
    };
  }, [snap.sex, snap.appearance?.top, snap.appearance?.bottom, contextSex]);

  useEffect(() => {
    applyPoseWeightsInternal(poseWeights);
  }, [poseWeights]);

  /* ---------- keep LIVE painter visible (and hide when empty) ---------- */
  useEffect(() => {
    const p = guidePaintRef.current;
    const mats = paintMatsRef.current;
    if (!p || !mats?.length) return;

    const apply = () => {
      const tex = p.getTexture?.();
      const has = !!tex;

      mats.forEach((m) => {
        m.map = has ? tex : null;
        m.opacity = has ? 1.0 : 0.0;
        m.transparent = true;
        m.alphaTest = 0.01;
        m.blending = THREE.NormalBlending;
        m.toneMapped = false;
        m.needsUpdate = true;
      });

      if (paintGroupRef.current) paintGroupRef.current.visible = has;
    };

    apply();
    window.addEventListener("guidepaint:toggle", apply);
    window.addEventListener("guidepaint:stroke", apply);
    window.addEventListener("guidepaint:clear", apply);
    return () => {
      window.removeEventListener("guidepaint:toggle", apply);
      window.removeEventListener("guidepaint:stroke", apply);
      window.removeEventListener("guidepaint:clear", apply);
    };
  }, []);

  // keep paint tex offsets stable and sync morphs every frame
  useFrame((_, delta) => {
    // ✅ LIVE DRAG PREVIEW (no composite rebuild)
    {
      const d = window.__tt_decal_drag;

      // --- iPad: force same "drag quality" behavior as desktop ---
      if (isIPad()) {
        const hasHit = Array.isArray(d?.hitWorld) && d.hitWorld.length === 3;

        // Only treat as "mesh drag" when hitWorld exists.
        // Decal editor drag often has url/active but no hitWorld — ignore it here.
        if (hasHit) {
          const [x, y, z] = d.hitWorld;
          const last = lastHitWorldRef.current;

          const moved =
            Math.abs(x - last[0]) +
              Math.abs(y - last[1]) +
              Math.abs(z - last[2]) >
            0.0005;

          lastHitWorldRef.current = [x, y, z];

          // require ~40ms of real movement before dropping quality
          if (moved) movingMsRef.current += delta * 1000;
          else
            movingMsRef.current = Math.max(
              0,
              movingMsRef.current - delta * 1000
            );

          if (movingMsRef.current > 40) {
            if (tattoo.renderQuality !== "drag") {
              tattoo.renderQuality = "drag";
              weForcedDragRef.current = true;
              weForcedDragRef.current.__t = performance.now();
            }
          }

          // return to high after movement stops for a moment
          if (weForcedDragRef.current) {
            const now = performance.now();
            const recentlyMoving = movingMsRef.current > 5;

            if (
              !recentlyMoving &&
              now - (weForcedDragRef.current.__t || now) > 160
            ) {
              tattoo.renderQuality = "high";
              tattoo.renderQualityTick = (tattoo.renderQualityTick || 0) + 1;

              weForcedDragRef.current = false;
              weForcedDragRef.current.__t = 0;
              movingMsRef.current = 0;
            }

            // keep extending the timer while moving
            if (recentlyMoving) weForcedDragRef.current.__t = now;
          }
        } else {
          // no hitWorld => not mesh-drag (likely decal editor). do not touch quality.
          movingMsRef.current = 0;
        }
      }

      const mesh = dragPreviewMeshRef.current;
      const mat = dragPreviewMatRef.current;
      if (!mesh || !mat) {
        // nothing
      } else if (!d?.active || !d?.layerId) {
        mesh.visible = false;
      } else {
        const L = (layers || []).find((x) => x?.id === d.layerId);
        const url = d?.url || L?.url || null; // ✅ prefer drag.url first

        if (!url) {
          mesh.visible = false;
        } else {
          // Decide whether preview should be low-res (iPad + dragging)
          const wantLow = isIPad() && dragLatchRef.current;

          const wantUrl = url;

          // If url changed, nuke caches for that mode
          if (wantLow) {
            if (dragPreviewUrlLoRef.current !== wantUrl) {
              dragPreviewUrlLoRef.current = wantUrl;

              // dispose old low tex
              try {
                dragPreviewTexLoRef.current?.dispose?.();
              } catch {}
              dragPreviewTexLoRef.current = null;

              // async load low-res
              loadResizedTexture(wantUrl, 768, { forceCanvas: true })
                .then((tex) => {
                  // standardize texture settings
                  tex.flipY = false;
                  tex.colorSpace = THREE.SRGBColorSpace;
                  tex.generateMipmaps = false;
                  tex.minFilter = THREE.LinearFilter;
                  tex.magFilter = THREE.LinearFilter;
                  tex.anisotropy = 1;

                  // cache
                  dragPreviewTexLoRef.current = tex;

                  // only apply if still same url + still want low
                  if (
                    dragPreviewUrlLoRef.current === wantUrl &&
                    (dragLatchRef.current || d?.active)
                  ) {
                    mat.map = tex;
                    mat.needsUpdate = true;
                  }
                })
                .catch(() => {
                  mesh.visible = false;
                });
            }

            // apply cached low tex if present
            if (dragPreviewTexLoRef.current) {
              mat.map = dragPreviewTexLoRef.current;
              mat.needsUpdate = true;
            }
          } else {
            // High-res mode (on release)
            if (dragPreviewUrlHiRef.current !== wantUrl) {
              dragPreviewUrlHiRef.current = wantUrl;

              try {
                dragPreviewTexHiRef.current?.dispose?.();
              } catch {}
              dragPreviewTexHiRef.current = null;

              const loader = new THREE.TextureLoader();
              loader.setCrossOrigin?.("anonymous");
              loader.load(
                wantUrl,
                (tex) => {
                  tex.flipY = false;
                  tex.colorSpace = THREE.SRGBColorSpace;
                  tex.generateMipmaps = false;
                  tex.minFilter = THREE.LinearFilter;
                  tex.magFilter = THREE.LinearFilter;
                  tbaseTex.anisotropy = 1;

                  dragPreviewTexHiRef.current = tex;

                  if (
                    dragPreviewUrlHiRef.current === wantUrl &&
                    !(dragLatchRef.current || d?.active)
                  ) {
                    mat.map = tex;
                    mat.needsUpdate = true;
                  }
                },
                undefined,
                () => {
                  mesh.visible = false;
                }
              );
            }

            if (dragPreviewTexHiRef.current) {
              mat.map = dragPreviewTexHiRef.current;
              mat.needsUpdate = true;
            }
          }

          // POSITION: you are already sending hitWorld — use it
          // IMPORTANT: hitWorld is WORLD space; mesh is parented to root => convert to root local
          if (Array.isArray(d.hitWorld) && d.hitWorld.length === 3) {
            const v = tmpV3Ref.current;
            v.set(d.hitWorld[0], d.hitWorld[1], d.hitWorld[2]);

            rootRef.current?.updateWorldMatrix?.(true, false);
            rootRef.current?.worldToLocal?.(v);

            mesh.position.copy(v);
          } else {
            // no hitWorld => can't place it reliably
            mesh.visible = false;
            return;
          }

          // scale/rot (cheap but works)
          const t = L?.transform || {};
          const sx = typeof t.scaleX === "number" ? t.scaleX : t.scale ?? 0.25;
          const sy = typeof t.scaleY === "number" ? t.scaleY : t.scale ?? 0.25;
          mesh.scale.set(sx, sy, 1);

          // face camera and apply rot
          mesh.quaternion.copy(camera.quaternion);
          mesh.rotateZ(t.rot || 0);

          mesh.visible = true;
        }
      }
    }
    const p = guidePaintRef.current;
    const tex = p?.getTexture?.();
    if (
      tex &&
      (tex.offset.x !== 0 ||
        tex.offset.y !== 0 ||
        tex.repeat.x !== 1 ||
        tex.repeat.y !== 1)
    ) {
      tex.offset.set(0, 0);
      tex.repeat.set(1, 1);
      tex.needsUpdate = true;
    }

    const A = baseMeshesRef.current;
    const B = overlayMeshesRef.current;
    const C = paintMeshesRef.current;
    const D = whiteOverlayMeshesRef.current;
    const U = underlayMeshesRef.current;

    for (let i = 0; i < A.length; i++) {
      const a = A[i];
      const inf = a?.morphTargetInfluences;
      if (!inf) continue;

      // --- fast “did anything change?” check (sample a few indices) ---
      // (avoids full copy when nothing is moving)
      const len = inf.length;
      if (!len) continue;

      const s0 = inf[0] || 0;
      const s1 = inf[(len / 3) | 0] || 0;
      const s2 = inf[((2 * len) / 3) | 0] || 0;
      const s3 = inf[len - 1] || 0;

      // small hash string; stable enough for this purpose
      const hash = `${len}|${s0.toFixed(4)}|${s1.toFixed(4)}|${s2.toFixed(
        4
      )}|${s3.toFixed(4)}`;

      if (lastMorphHashRef.current[i] === hash) {
        continue; // ✅ nothing changed -> skip copying entirely
      }
      lastMorphHashRef.current[i] = hash;

      // --- copy influences only when changed ---
      const targets = [B[i], C[i], D[i], U[i]].filter(Boolean);
      for (const t of targets) {
        const tinf = t?.morphTargetInfluences;
        if (!tinf || tinf.length !== len) continue;

        for (let k = 0; k < len; k++) tinf[k] = inf[k];
      }
    }
  });

  //* ---------- FILTER LAYERS BY CONTEXT ---------- */
  // 1) filteredLayers
  const filteredLayers = useMemo(() => {
    if (!Array.isArray(layers) || (!contextSex && !contextPart))
      return layers || [];
    return layers.filter((L) => {
      const okSex = L.sex ? L.sex === contextSex : true;
      const okPart = L.part ? L.part === contextPart : true;
      return okSex && okPart && L.visible !== false;
    });
  }, [layers, contextSex, contextPart]);

  console.log(
    "[composite] filteredLayers",
    filteredLayers.map((l) => ({
      id: l.id,
      type: l.type,
      url: l.url,
      visible: l.visible,
      part: l.part,
      sex: l.sex,
      blend: l.blend,
    }))
  );

  const L = filteredLayers?.[0]; // you only have one merged layer in this case

  console.log("[merge reload] url len", L?.url?.length, L?.url?.slice(0, 32));

  if (L?.url?.startsWith("data:image")) {
    const img = new Image();
    img.onload = () =>
      console.log("[merge reload] img OK", img.naturalWidth, img.naturalHeight);
    img.onerror = (e) => console.log("[merge reload] img FAIL", e);
    img.src = L.url;
  }

  // 2) signature (depends on filteredLayers)
  const signature = useMemo(
    () => buildLayersSignature(partKey, filteredLayers, edgeClip),
    [partKey, filteredLayers, edgeClip]
  );

  /* ---------- BUILD COMPOSITE (base + optional white overlay) ---------- */
  useEffect(() => {
    if (!filteredLayers || filteredLayers.length === 0) {
      if (overlayGroupRef.current) overlayGroupRef.current.visible = false;
      if (whiteOverlayGroupRef.current)
        whiteOverlayGroupRef.current.visible = false;
      return;
    }
    // PartModel.jsx (near composite rebuild)

    let cancelled = false;

    // ✅ since we never run during drag, always do full rebuild settings
    const delay = 0;
    console.log("[PartModel] ttQuality =", ttQuality);
    const texSize = pickCompositeSize({ quality, gl });
    console.log("[TT] composite", {
      quality,
      texSize,
      device: isIPad() ? "ipad" : isProbablyMobile() ? "mobile" : "desktop",
    });
    console.log("[PartModel] composite size", {
      quality,
      texSize,
      qualityTick,
    });

    const timer = setTimeout(async () => {
      try {
        console.log("[PartModel] composite size", {
          ttQuality,
          texSize,
          qualityTick,
        });
        const result = await buildPartComposite(partKey, filteredLayers, {
          size: texSize,
          edgeClip,
          dragPreview: null, // ✅ not used in composite rebuild
        });
        if (cancelled) return;

        let baseCanvas = result;
        let overlayCanvas = null;
        let underlayCanvas = null;
        

        if (result && result.base && result.overlay) {
          baseCanvas = result.base;
          overlayCanvas = result.overlay;
          underlayCanvas = result.underlay || null;
        }

        try {
          const uctx = underlayCanvas?.getContext?.("2d");
          if (uctx) {
            const p = Array.from(uctx.getImageData(1163, 670, 1, 1).data);
            console.log("[UNDERLAY pixel @1163,670]", p);
          }
        } catch (e) {
          console.log("underlay pixel read failed", e);
        }
        try {
          const ctx = baseCanvas?.getContext?.("2d");
          if (ctx) {
            // pick a pixel you KNOW is empty background in the composite
            const p = Array.from(ctx.getImageData(10, 10, 1, 1).data);
            console.log("[BASE pixel @10,10]", p); // [r,g,b,a]
          }
        } catch (e) {
          console.log("base pixel read failed", e);
        }
        console.log(
          "[TT underlay]",
          !!underlayCanvas,
          underlayCanvas?.width,
          underlayCanvas?.height
        );

        // UNDERLAY (bind texture, keep hidden for now)
        if (underlayCanvas && underlayMatsRef.current?.length) {
          const underlayTex = canvasToTexture(underlayCanvas);

          underlayMatsRef.current.forEach((m) => {
            m.map = underlayTex;

            // TEMP DEBUG "scream"
            m.transparent = true;
            m.opacity = 1.0;

            m.alphaTest = 0.1;
            m.toneMapped = false;
            m.needsUpdate = true;
          });

          if (underlayGroupRef.current) underlayGroupRef.current.visible = true;
        }

        const baseTex = canvasToTexture(baseCanvas);
        baseTex.anisotropy = 1;
        baseTex.needsUpdate = true;
        if (
          currentOverlayTexRef.current &&
          currentOverlayTexRef.current !== baseTex
        ) {
          currentOverlayTexRef.current.dispose?.();
        }
        currentOverlayTexRef.current = baseTex;

        overlayMatsRef.current.forEach((m) => {
          m.map = baseTex;
          m.transparent = true;
          m.opacity = 1.0;
          m.alphaTest = 0.01;
          m.blending = THREE.MultiplyBlending;
          m.toneMapped = false;
          m.depthTest = true;
          m.depthWrite = false;
          m.polygonOffset = true;
          m.polygonOffsetFactor = -1;
          m.polygonOffsetUnits = -1;
          m.needsUpdate = true;
        });
        if (overlayGroupRef.current) overlayGroupRef.current.visible = true;

        if (overlayCanvas) {
          const overlayTex = canvasToTexture(overlayCanvas);

          if (
            currentWhiteOverlayTexRef.current &&
            currentWhiteOverlayTexRef.current !== overlayTex
          ) {
            currentWhiteOverlayTexRef.current.dispose?.();
          }
          currentWhiteOverlayTexRef.current = overlayTex;

          whiteOverlayMatsRef.current.forEach((m) => {
            m.map = overlayTex;
            m.transparent = true;
            m.opacity = 1.0;
            m.alphaTest = 0.1;

            // IMPORTANT: for WhiteFill you want normal, not additive
            m.blending = THREE.NormalBlending;

            m.toneMapped = false;
            m.depthTest = true;
            m.depthWrite = false;
            m.needsUpdate = true;
          });

          if (whiteOverlayGroupRef.current)
            whiteOverlayGroupRef.current.visible = true;
        } else {
          if (whiteOverlayGroupRef.current)
            whiteOverlayGroupRef.current.visible = false;
        }
        try {
          stackedAoTexRef.current?.dispose?.();
          stackedAoTexRef.current = null;
        } catch {}
        try {
          setTimeout(() => {
            requestAnimationFrame(() => {
              window.dispatchEvent(
                new CustomEvent("tattoo:composite-ready", {
                  detail: { partKey, at: Date.now() },
                })
              );
            });
          }, 0);
        } catch {}
      } catch (e) {
        console.warn("Composite build failed:", e);
      }
    }, delay);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [signature, edgeClip, filteredLayers, partKey, quality, qualityTick, matsReadyTick]);

  /* ---------- legacy single-image path (optional) ---------- */
  useEffect(() => {
    if (!uvImage || (layers && layers.length)) return;
    const loader = new THREE.TextureLoader();
    loader.setCrossOrigin?.("anonymous");
    let cancelled = false;
    loader.load(
      uvImage,
      (tex) => {
        if (cancelled) return;
        tex.flipY = false;
        tex.colorSpace = THREE.SRGBColorSpace;
        tex.generateMipmaps = false;
        tex.minFilter = THREE.LinearFilter;
        tex.magFilter = THREE.LinearFilter;

        overlayMatsRef.current.forEach((m) => {
          m.map = tex;
          m.transparent = true;
          m.opacity = overlayOpacity;
          m.alphaTest = 0.05; // or 0.05 if you want a slightly harder cut
          m.blending = THREE.MultiplyBlending;
          m.toneMapped = false;

          m.depthTest = true;
          m.depthWrite = false;
          m.polygonOffset = true;
          m.polygonOffsetFactor = -1;
          m.polygonOffsetUnits = -1;

          m.needsUpdate = true;
        });
        if (overlayGroupRef.current) overlayGroupRef.current.visible = true;
      },
      undefined,
      (err) => console.warn("Tattoo texture load failed:", err)
    );
    return () => {
      cancelled = true;
    };
  }, [uvImage, overlayOpacity, edgeClip, tattooKey, layers]);

  return <group ref={rootRef} dispose={null} />;
});

export default PartModel;
